import { BLOG_URL } from "../data/categories";

const API_ROOT = `${BLOG_URL}/wp-json/wp/v2`;

// WordPress hands back titles and excerpts as rendered HTML with entities baked
// in ("Here&#8217;s how"). Decode via textarea and read .value so nothing is
// ever interpreted as markup.
function decodeEntities(html) {
  const el = document.createElement("textarea");
  el.innerHTML = html;
  return el.value;
}

function toPlainText(html) {
  return decodeEntities(String(html).replace(/<[^>]*>/g, ""))
    .replace(/\s+/g, " ")
    .trim();
}

// Post dates arrive without a timezone offset, so they're read as local time.
// That's fine here - this is a display string, not a calculation.
function formatDate(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// WordPress generates a crop per registered size. These are the ones with a
// sane landscape aspect ratio for a card - deliberately skipping `thumbnail`
// (square) and `cmplz_banner_image` (350x100), which object-fit would mangle.
// Ordered smallest first; the first hit becomes the 1x src.
const CARD_SIZES = [
  "bachelor-card",
  "medium_large",
  "large",
  "post-thumbnail",
];

function pickImage(media) {
  if (!media) {
    return { image: null, imageSrcSet: null, imageAlt: "" };
  }

  const sizes = media.media_details?.sizes ?? {};
  const usable = CARD_SIZES.map((name) => sizes[name]).filter(
    (size) => size?.source_url && size?.width
  );

  // Hand the browser every crop and let it choose by card width and DPR.
  const byWidth = new Map(usable.map((size) => [size.width, size.source_url]));
  const srcSet = [...byWidth.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([width, url]) => `${url} ${width}w`)
    .join(", ");

  return {
    image: usable[0]?.source_url ?? media.source_url ?? null,
    imageSrcSet: srcSet || null,
    imageAlt: toPlainText(media.alt_text ?? ""),
  };
}

function normalizePost(post) {
  const media = post._embedded?.["wp:featuredmedia"]?.[0];

  return {
    id: post.id,
    url: post.link,
    title: toPlainText(post.title?.rendered ?? ""),
    excerpt: toPlainText(post.excerpt?.rendered ?? ""),
    date: formatDate(post.date),
    ...pickImage(media),
  };
}

/**
 * Newest posts straight from the blog, so this page can never go stale.
 * Throws on a non-2xx response; callers are expected to show a fallback.
 */
export async function fetchLatestPosts(limit = 6, signal) {
  const params = new URLSearchParams({
    per_page: String(limit),
    _embed: "wp:featuredmedia",
    // _links must stay in _fields: WordPress resolves _embedded *from* the
    // links collection, so dropping it silently returns posts with no images.
    _fields: "id,link,title,date,excerpt,_links,_embedded",
  });

  const response = await fetch(`${API_ROOT}/posts?${params}`, { signal });
  if (!response.ok) {
    throw new Error(`Blog API responded with ${response.status}`);
  }

  const posts = await response.json();
  return Array.isArray(posts) ? posts.map(normalizePost) : [];
}
